import { FocusMonitor } from '@angular/cdk/a11y';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { convertToSecretCode, MonthlyLabels, QuarterLabels } from './quarter-selector.constants';
import {
  QuarterRangeSelectorState,
  QuarterRangeSelectorValue,
  QuarterSelectorType,
  QuarterSelectorValue,
} from './quarter-selector.model';
import { RangeQuarterSelectorComponent } from './range-quarter-selector.component';
import { RangeQuarterSelectorService } from './range-quarter-selector.service';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'cbre-miq-quarter-selector',
  styleUrl: 'quarter-selector.component.scss',
  templateUrl: 'quarter-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RangeQuarterSelectorService],
  imports: [NgClass, MatIcon],
})
export class QuarterSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly editable = input(true);
  readonly placeholder = input('');
  readonly type = input<QuarterSelectorType>(QuarterSelectorType.Quarter);
  readonly value = input<QuarterRangeSelectorValue>();
  readonly singleSelection = input<boolean>(null);
  readonly requireYear = input(true);
  readonly marketQtdVal = input<QuarterSelectorValue | Date>();
  readonly minValue = input<QuarterSelectorValue | Date>();
  readonly maxValue = input<QuarterSelectorValue | Date>();
  readonly showAsQtd = input(false);
  readonly allowedPeriods = input<QuarterSelectorValue[]>();
  readonly restrictedPeriods = input<QuarterSelectorValue[]>();
  readonly displayClearButton = input(false);
  readonly isClearFilterEnabled = input(false);
  readonly showSelector = input(true);
  readonly valueChange = output<QuarterRangeSelectorValue>();
  readonly clearFilterEmitter = output<void>();

  readonly wrapperElement = viewChild<ElementRef>('wrapper');

  public displayValue = '';
  public displayValueSet = false;
  public isDialogOpen = false;
  public allowThisPeriodsOnly: Set<number> = null;
  public restrictThisPeriodsOnly: Set<number> = null;
  private minProcessedValue: QuarterSelectorValue;
  private maxProcessedValue: QuarterSelectorValue;
  private readonly defaultMinValue: QuarterSelectorValue = { periodValue: 1, periodYear: 1950 };
  private readonly defaultMaxValue: QuarterSelectorValue = { periodValue: 12, periodYear: 2050 };
  private readonly subscription: Subscription = new Subscription();
  public marketQtdValue: QuarterSelectorValue | Date;
  public displayOverlay = true;
  private _value: QuarterRangeSelectorValue;

  private readonly element = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  public stateService = inject(RangeQuarterSelectorService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Replaces both ngOnInit and ngOnChanges calls to onMaxValueHasData
    effect(() => {
      this.onMaxValueHasData();
    });
  }

  ngAfterViewInit() {
    this.focusMonitor
      .monitor(this.wrapperElement(), true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(origin => {
        if (origin && !this.isClearFilterEnabled() && this.showSelector()) {
          this.onElementClick();
        }
      });
  }

  ngOnInit() {
    // Initial setup handled by the effect in the constructor
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onElementClick() {
    if (!!this.editable() && this.displayOverlay) {
      this.createOverlay();
    }
    if (!this.displayOverlay) {
      this.displayValue = '';
      this.displayOverlay = true;
    }
  }

  private onMaxValueHasData() {
    if (this.marketQtdVal() || this.maxValue()) {
      this.marketQtdValue = this.marketQtdVal() || this.maxValue();
    }
    this.setNewStateFromOutside();
  }
  private setNewStateFromOutside() {
    this.processRestrictedAndAllowedPeriods();
    this.processMinAndMaxValues();
    const newState: QuarterRangeSelectorState = {
      selectorType: this.type(),
      requiredYear: this.requireYear(),
      ...this.value(),
      minValue: this.minProcessedValue ? this.minProcessedValue : this.defaultMinValue,
      maxValue: this.maxProcessedValue ? this.maxProcessedValue : this.defaultMaxValue,
      singleSelection: this.singleSelection(),
      showAsQtd: this.showAsQtd(),
      allowedPeriods: this.allowThisPeriodsOnly,
      restrictedPeriods: this.restrictThisPeriodsOnly,
      marketQtdValue: this.marketQtdValue,
    };
    this.stateService.setInitialState(newState);
    // If the state is invalid, we change the reference.
    if (!this.stateService.isAValidState) {
      this.emitReference();
    }
    this.refreshLabel();
  }

  private processRestrictedAndAllowedPeriods() {
    const periodsToCheck: QuarterSelectorValue[] = [...(this.allowedPeriods() || []), ...(this.restrictedPeriods() || [])];
    const uniquePeriods = new Set();
    for (const period of periodsToCheck) {
      const keyToCheck = period.periodYear + '' + period.periodValue;
      if (uniquePeriods.has(keyToCheck)) {
        console.error(`Quarter selector: collision detected for period ${keyToCheck} `);
        this.allowThisPeriodsOnly = null;
        this.restrictThisPeriodsOnly = null;
        break;
      } else {
        uniquePeriods.add(keyToCheck);
      }
    }
    if (uniquePeriods.size === periodsToCheck.length) {
      this.allowThisPeriodsOnly = this.allowedPeriods()
        ? new Set(
            this.allowedPeriods().map(period => convertToSecretCode(period.periodYear, period.periodValue, this.type()))
          )
        : null;
      this.restrictThisPeriodsOnly = this.restrictedPeriods()
        ? new Set(
            this.restrictedPeriods().map(period => convertToSecretCode(period.periodYear, period.periodValue, this.type()))
          )
        : null;
    }
  }

  private processMinAndMaxValues() {
    this.minProcessedValue = this.processLimitDate(this.minValue());
    this.maxProcessedValue = this.processLimitDate(this.maxValue());
  }

  private processLimitDate(date: Date | QuarterSelectorValue): QuarterSelectorValue {
    const isDate = date && typeof (date as Date).getTime === 'function';
    if (isDate) {
      const month = (date as Date).getMonth() + 1;
      if (this.type() === QuarterSelectorType.Monthly) {
        return {
          periodYear: (date as Date).getFullYear(),
          periodValue: month,
        };
      } else {
        const value = Math.ceil(month / 3);
        return {
          periodYear: (date as Date).getFullYear(),
          periodValue: value,
        };
      }
    } else {
      return date as QuarterSelectorValue;
    }
  }

  private createOverlay() {
    if (!this.isDialogOpen) {
      const overlayRef = this.overlay.create(this.getOverlayConfig());
      const componentToRender = new ComponentPortal(RangeQuarterSelectorComponent, null, this.injector);
      overlayRef.attach(componentToRender);
      this.subscription.add(
        overlayRef.backdropClick().subscribe(() => {
          this.closeOverlay(overlayRef);
        })
      );
      this.subscription.add(
        this.stateService.onFinishSelection().subscribe(() => {
          this.refreshLabel();
        })
      );
      this.subscription.add(
        this.stateService.onClose().subscribe(() => {
          this.closeOverlay(overlayRef);
        })
      );
      this.isDialogOpen = true;
      this.cd.markForCheck();
    }
  }

  private closeOverlay(overlayRef) {
    overlayRef.dispose();
    this.isDialogOpen = false;
    this.refreshLabel();
    this.emitReference();
  }

  private refreshLabel() {
    this.displayValue = '';
    const state = this.stateService.getState();
    const stateIsEmpty = state.eq == null && state.lte == null && state.gte == null;
    if (this.stateService.isAValidState && stateIsEmpty) {
      this.displayValueSet = false;
      return;
    }

    if (state.eq != null || state.gte != null) {
      const value = state.eq || state.gte;
      const labeledValue = this.getLabeledValue(state.selectorType, value.periodValue);
      this.displayValue = `${labeledValue} ${this.requireYear() ? value.periodYear : ''}`;
    }

    this.whenLteIsNullAndValidIsMax(state);

    this.displayValueSet = true;
    this.cd.markForCheck();
  }

  private whenLteIsNullAndValidIsMax(state: QuarterRangeSelectorState): void {
    if (state.lte != null && this.stateService.isMaxStateValid) {
      const lteLabeledValue = this.getLabeledValue(state.selectorType, state.lte.periodValue);
      const lteDisplay = `${lteLabeledValue} ${this.requireYear() ? state.lte.periodYear : ''}`;
      this.displayValue = `${this.displayValue} - ${lteDisplay}`;
    }
  }

  private getLabeledValue(selectorType: QuarterSelectorType, value: number): string {
    if (selectorType != QuarterSelectorType.Yearly) {
      let mapType: Map<number, string>;
      switch (selectorType) {
        case QuarterSelectorType.Quarter:
          mapType = QuarterLabels;
          break;
        case QuarterSelectorType.Monthly:
          mapType = MonthlyLabels;
          break;
        default:
          mapType = QuarterLabels;
      }
      if (mapType.has(value)) {
        return mapType.get(value);
      } else {
        console.error(`Incorrect value for the given type. value: ${value}, type: ${selectorType}`);
        return '';
      }
    }
    return '';
  }

  private emitReference() {
    if (this.stateService.isAValidState) {
      const { eq, lte, gte } = this.stateService.getState();
      this._value = { eq, lte, gte };
    } else {
      this._value = null;
    }
    this.valueChange.emit(this._value);
  }

  private getOverlayConfig() {
    const overlayConfiguration: OverlayConfig = {
      maxHeight: 160,
      width: 250,
      hasBackdrop: true,
    };

    overlayConfiguration.backdropClass = 'quarter-selector-backdrop-overlay';
    overlayConfiguration.positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.element)
      .withPositions([
        // bottom
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        // top
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);
    overlayConfiguration.scrollStrategy = this.overlay.scrollStrategies.block();
    return overlayConfiguration;
  }

  clearFilter() {
    this.displayOverlay = false;
    this.clearFilterEmitter.emit();
  }
}
